
import React, { useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { useToast } from "@/components/ui/use-toast";
import { CategoryFilter } from "./CategoryFilter";
import { SortControls } from "./SortControls";
import { Pagination } from "./Pagination";
import { ReportGrid } from "./ReportGrid";
import { NoResults } from "./NoResults";
import ReportDetails from "@/components/ReportDetails";
import { useSupportMutation } from "@/hooks/useSupportMutation";
import { useMobileFilters } from "@/hooks/useMobileFilters";
import { useReportFilters } from "@/hooks/report-filters";
import { Skeleton } from "@/components/ui/skeleton";
import { Button } from "@/components/ui/button";
import { ChevronDown, Filter } from "lucide-react";
import { motion } from "framer-motion";

interface ReportListContainerProps {
  searchTerm: string;
}

const ReportListContainer = ({ searchTerm }: ReportListContainerProps) => {
  const [selectedReport, setSelectedReport] = useState<any | null>(null);
  const { toast } = useToast();
  const queryClient = useQueryClient();
  
  // Custom hooks
  const { showFilters, setShowFilters, isMobile } = useMobileFilters();

  // Utilisez useReportFilters pour gérer l'état du filtre et de la pagination
  const {
    selectedCategory, 
    setSelectedCategory,
    sortBy, 
    setSortBy,
    sortOrder, 
    setSortOrder,
    currentPage, 
    setCurrentPage,
    itemsPerPage, 
    setItemsPerPage,
    data,
    isLoading,
    filteredReports,
    totalPages,
    handleCategoryChange,
    handleSortChange,
    handleItemsPerPageChange,
    handlePaginationHover
  } = useReportFilters({ searchTerm, queryClient, toast });

  // Mutation pour supporter un signalement
  const supportMutation = useSupportMutation({ queryClient, toast });

  // Afficher un squelette pendant le chargement des données
  if (isLoading) {
    return (
      <div className="space-y-6">
        <div className="flex flex-col gap-4">
          <div className="space-y-2">
            <Skeleton className="h-5 w-40" />
            <div className="flex gap-2">
              {[1, 2, 3, 4, 5].map((i) => (
                <Skeleton key={i} className="h-10 w-24" />
              ))}
            </div>
          </div>
          <div className="flex gap-2">
            <Skeleton className="h-10 w-24" />
            <Skeleton className="h-10 w-24" />
          </div>
        </div>
        <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
          {Array.from({ length: 6 }).map((_, index) => (
            <Skeleton key={index} className="h-64 w-full rounded-lg" />
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {isMobile ? (
        <div className="mb-4">
          <Button 
            variant="outline" 
            onClick={() => setShowFilters(!showFilters)}
            className="w-full flex items-center justify-between"
          >
            <span className="flex items-center gap-2">
              <Filter className="h-4 w-4" />
              Filtres et tri
            </span>
            <ChevronDown className={`h-4 w-4 transition-transform duration-200 ${showFilters ? 'rotate-180' : ''}`} />
          </Button>
          
          {showFilters && (
            <motion.div 
              initial={{ height: 0, opacity: 0 }}
              animate={{ height: 'auto', opacity: 1 }}
              exit={{ height: 0, opacity: 0 }}
              transition={{ duration: 0.3 }}
              className="mt-4 p-4 border rounded-lg space-y-4"
            >
              <CategoryFilter
                selectedCategory={selectedCategory}
                onCategoryChange={handleCategoryChange}
              />
              <SortControls
                sortBy={sortBy}
                sortOrder={sortOrder}
                onSortChange={handleSortChange}
              />
            </motion.div>
          )}
        </div>
      ) : (
        <div className="flex flex-col gap-4">
          <CategoryFilter
            selectedCategory={selectedCategory}
            onCategoryChange={handleCategoryChange}
          />
          <SortControls
            sortBy={sortBy}
            sortOrder={sortOrder}
            onSortChange={handleSortChange}
          />
        </div>
      )}

      {filteredReports.length === 0 ? (
        <NoResults 
          selectedCategory={selectedCategory} 
          setShowCreate={() => {}} // Fournir une fonction vide pour éviter l'erreur
        />
      ) : (
        <ReportGrid 
          reports={filteredReports}
          onReportClick={setSelectedReport}
          onSupportClick={reportId => supportMutation.mutate(reportId)}
        />
      )}

      <Pagination
        currentPage={currentPage}
        totalPages={totalPages}
        onPageChange={setCurrentPage}
        onHover={handlePaginationHover}
        itemsPerPage={itemsPerPage}
        onItemsPerPageChange={handleItemsPerPageChange}
      />

      {selectedReport && (
        <ReportDetails
          report={selectedReport}
          isOpen={!!selectedReport}
          onClose={() => setSelectedReport(null)}
          onSupport={() => supportMutation.mutate(selectedReport.id)}
        />
      )}
    </div>
  );
};

export default ReportListContainer;
